import { Mark, mergeAttributes } from "@tiptap/core"
import type { Attrs, Mark as ProsemirrorMark, Schema } from "prosemirror-model"

import { type LabelElement, LabelExtension } from "./LabelModel"

export interface LabelProtocolOptions {
  scheme: string
  optionalSlashes?: boolean
}

export interface LabelOptions {
  /** These are the values passed into the HTML through the Tiptap extension. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  htmlAttributes: Record<string, any>
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    Label: {
      setLabel: (attributes: { label: string }) => ReturnType
      unsetLabel: () => ReturnType
    }
  }
}

const componentName = "label"

const TTLabel = Mark.create<LabelOptions>({
  name: componentName,
  priority: 1000,
  keepOnSplit: false,
  inclusive: false,

  addOptions() {
    return {
      htmlAttributes: {
        // to set border color with opacity, rgba is required: https://stackoverflow.com/questions/4062001/can-you-set-a-border-opacity-in-css
        style:
          "background-color: hsl(251, 89%, 96%); border: 1px dashed rgba(68, 53, 146, 0.4); border-radius: 0px 2px 2px 2px",
      },
    }
  },

  addAttributes() {
    return {
      label: {
        default: "",
      },
    }
  },

  addCommands() {
    return {
      setLabel:
        (attributes) =>
        ({ chain }) => {
          return chain()
            .unsetMark(this.name, { extendEmptyMarkRange: true })
            .setMark(this.name, attributes)
            .run()
        },
      unsetLabel:
        () =>
        ({ chain }) => {
          return chain()
            .unsetMark(this.name, { extendEmptyMarkRange: true })
            .setMeta("preventAutolink", true)
            .run()
        },
    }
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(this.options.htmlAttributes, HTMLAttributes),
      0,
    ]
  },
})

const converter = {
  fromPrismic(schema: Schema) {
    return function (elmt: LabelElement): ProsemirrorMark | undefined {
      const data = elmt.data
      const attrs = {
        type: TTLabel.name,
        label: data,
      }

      return schema.marks[TTLabel.name]?.create(attrs)
    }
  },

  toPrismic(label: LabelMark): string {
    return label.attrs.label
  },
}

interface LabelMarkAttributes extends Attrs {
  label: string
}

interface LabelMark extends ProsemirrorMark {
  attrs: LabelMarkAttributes
}

export const Label = new LabelExtension(componentName, TTLabel, converter)
